import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import { matchPathname } from '../../util/routes';
import {
  isAnyFilterActive,
  parseSelectFilterOptions,
  constructQueryParamName,
  getQueryParamNames,
} from '../../util/search';
import { parse, stringify } from '../../util/urlHelpers';
import {
  getStartOf,
  parseDateFromISO8601,
  subtractTime,
  addTime,
  stringifyDateToISO8601,
} from '../../util/dates';
import { isFieldForCategory, isFieldForListingType } from '../../util/fieldHelpers';
import { stripIgnoredSearchQueryParams } from '../../util/searchQueryParams';

export const getSearchPageDefaultFiltersConfig = (
  defaultFiltersConfig = [],
  listingTypePathParam = null
) => {
  const configuredFilters = Array.isArray(defaultFiltersConfig) ? defaultFiltersConfig : [];
  const listingTypeFiltered = listingTypePathParam
    ? configuredFilters.filter(filterConfig => filterConfig.key !== 'listingType')
    : configuredFilters;

  // Dates filter is intentionally hidden on SearchPage.
  return listingTypeFiltered.filter(filterConfig => filterConfig.schemaType !== 'dates');
};

const validURLParamForCategoryData = (prefix, categories, level, params) => {
  const levelKey = constructQueryParamName(`${prefix}${level}`, 'public');
  const legacyCategoryParamName = constructQueryParamName('category', 'public');
  const legacyCategoryValue =
    typeof params?.[legacyCategoryParamName] !== 'undefined'
      ? `${params?.[legacyCategoryParamName]}`
      : undefined;
  const levelValue =
    typeof params?.[levelKey] !== 'undefined'
      ? `${params?.[levelKey]}`
      : level === 1
      ? legacyCategoryValue
      : undefined;

  const foundCategory = categories.find(cat => cat.id === levelValue);
  const subcategories = foundCategory?.subcategories || [];
  return foundCategory && subcategories.length > 0
    ? {
        [levelKey]: levelValue,
        ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
      }
    : foundCategory
    ? { [levelKey]: levelValue }
    : {};
};

const validURLParamForListingTypeData = (listingTypes, param) => {
  const listingTypeValue = param?.pub_listingType;
  const foundListingType = listingTypes.find(lt => lt === listingTypeValue);
  return foundListingType && listingTypeValue ? { pub_listingType: listingTypeValue } : {};
};

const currentListingTypeValues = (
  currentPathParams,
  validListingTypeParamNames,
  activeListingTypes
) => {
  const { listingType: listingTypePathParam } = currentPathParams || {};
  if (listingTypePathParam) {
    return [listingTypePathParam];
  }
  const listingTypeParams = Object.values(validListingTypeParamNames);
  return listingTypeParams.length > 0 ? listingTypeParams : activeListingTypes || [];
};

/**
 * Omit those listing field parameters, that are not allowed with current category selection
 *
 * @param {Object} searchParams current search params
 * @param {Object} filterConfigs contains listingFieldsConfig and defaultFiltersConfig.
 * @returns search parameters without currently restricted listing fields
 */
export const omitLimitedListingFieldParams = (searchParams, filterConfigs) => {
  const {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
    activeListingTypes,
    currentPathParams = {},
  } = filterConfigs;

  const categorySearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
  const listingTypeSearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'listingType');
  // Fall back to the built-in 'categoryLevel' key when the category filter UI is
  // disabled — pub_category may still be in the URL and listing fields with
  // limitToCategoryIds need it to evaluate isFieldForCategory correctly.
  const categoryKey = categorySearchConfig?.key || 'categoryLevel';
  const validNestedCategoryParamNames =
    listingCategories && listingCategories.length > 0
      ? validURLParamForCategoryData(categoryKey, listingCategories, 1, searchParams)
      : {};

  const validListingTypeParamNames =
    activeListingTypes && listingTypeSearchConfig
      ? validURLParamForListingTypeData(activeListingTypes, searchParams)
      : {};

  return Object.entries(searchParams).reduce((picked, searchParam) => {
    const [searchParamKey, searchParamValue] = searchParam;
    const foundConfig = listingFieldsConfig.find(
      f => constructQueryParamName(f.key, f.scope) === searchParamKey
    );
    const currentCategories = Object.values(validNestedCategoryParamNames);
    const isForCategory = isFieldForCategory(currentCategories, foundConfig);
    const currentListingType = currentListingTypeValues(
      currentPathParams,
      validListingTypeParamNames,
      activeListingTypes
    );
    const isForListingType = isFieldForListingType(currentListingType, foundConfig);
    const searchParamMaybe =
      !foundConfig || (foundConfig && isForCategory && isForListingType)
        ? { [searchParamKey]: searchParamValue }
        : {};
    return { ...picked, ...searchParamMaybe };
  }, {});
};

/**
 * Validates a filter search param against the default and extended data configuration of listings.
 *
 * All invalid param names and values are dropped
 *
 * @param {String} queryParamName Search parameter name
 * @param {Object} paramValue Search parameter value
 * @param {Object} listingFieldFilters extended data configuration with indexForSearch === true
 * @param {Object} defaultFilters configuration for default built-in filters.
 */
export const validURLParamForExtendedData = (
  queryParamName,
  paramValueRaw,
  listingFieldFilters,
  defaultFilters
) => {
  const paramValue = paramValueRaw.toString();

  // Price is built-in filter for listing entities
  if (queryParamName === 'price') {
    // Restrict price range to correct min & max
    const { min, max } = defaultFilters.find(conf => conf.schemaType === 'price') || {};
    const valueArray = paramValue ? paramValue.split(',') : [];
    const validValues = valueArray.map(v => {
      return v < min ? min : v > max ? max : v;
    });
    return validValues.length === 2 ? { [queryParamName]: validValues.join(',') } : {};
  } else if (queryParamName === 'keywords') {
    return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'dates') {
    const searchTZ = 'Etc/UTC';
    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const dates = paramValue ? paramValue.split(',') : [];
    const hasValues = dates.length > 0;
    const startDate = hasValues ? parseDateFromISO8601(dates[0], searchTZ) : null;
    const endDate = hasValues ? parseDateFromISO8601(dates[1], searchTZ) : null;
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    return hasValidDates ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'seats') {
    return paramValue ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'pub_listingType') {
    return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
  }

  // Resolve configurations for extended data filters
  const listingFieldFilterConfig = listingFieldFilters.find(
    f => queryParamName === constructQueryParamName(f.key, f.scope)
  );

  if (listingFieldFilterConfig) {
    const { schemaType, enumOptions = [], filterConfig } = listingFieldFilterConfig;
    if ([SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM].includes(schemaType)) {
      const isSchemaTypeMultiEnum = schemaType === SCHEMA_TYPE_MULTI_ENUM;
      const searchMode = filterConfig?.searchMode;

      // Pick valid select options only
      const valueArray = parseSelectFilterOptions(paramValue);
      const allowedValues = enumOptions
        .map(o => (typeof o?.option !== 'undefined' ? o.option : o?.key))
        .filter(v => v != null)
        .map(v => `${v}`);
      const validValues = valueArray.filter(v => allowedValues.includes(v)).join(',');

      return validValues.length > 0
        ? {
            [queryParamName]:
              isSchemaTypeMultiEnum && searchMode ? `${searchMode}:${validValues}` : validValues,
          }
        : {};
    } else {
      // Generic filter - remove empty params
      return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
    }
  }
  return {};
};

/**
 * Checks filter param value validity.
 *
 * The URL params that are not part of listing.query filters are dropped by default.
 *
 * @param {Object} params Search query params
 * @param {Object} filterConfigs contains listingFieldsConfig and defaultFiltersConfig.
 * @param {boolean} dropNonFilterParams if false, extra params are passed through.
 */
export const validFilterParams = (params, filterConfigs, dropNonFilterParams = true) => {
  const { listingFieldsConfig, defaultFiltersConfig, listingCategories } = filterConfigs;

  const listingFieldFiltersConfig = listingFieldsConfig.filter(
    config => config.filterConfig?.indexForSearch
  );
  const listingFieldParamNames = listingFieldFiltersConfig.map(f =>
    constructQueryParamName(f.key, f.scope)
  );
  // Note: builtInFilterParamNames might include categoryLevel,
  //       even though it isn't a paramname that's used with nested category tree.
  //       (pub_categoryLevel1, pub_categoryLevel2, and pub_categoryLevel3 are used instead.)
  const builtInFilterParamNames = defaultFiltersConfig.map(f => {
    return ['category', 'listingType'].includes(f.schemaType) ? `pub_${f.key}` : f.key;
  });
  const filterParamNames = [...listingFieldParamNames, ...builtInFilterParamNames];

  // Note: currently, we only support nested enums with a single default filter
  //       that has schema type: "category"
  const categorySearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
  const validNestedCategoryParamNames = categorySearchConfig
    ? validURLParamForCategoryData(categorySearchConfig.key, listingCategories, 1, params)
    : {};
  const isParamNameNestedEnumRelated = (paramName, key, isNestedEnum) => {
    return isNestedEnum && key ? paramName.indexOf(key) > -1 : false;
  };

  // search params without category-restricted params
  const unlimitedSearchParams = omitLimitedListingFieldParams(params, filterConfigs);
  const paramEntries = Object.entries(unlimitedSearchParams);

  const listingFieldsAndBuiltInFilterParamNames = paramEntries.reduce((validParams, entry) => {
    const [paramName, paramValue] = entry;
    const isIndependentParam = filterParamNames.includes(paramName);
    const isNestedEnum = isIndependentParam
      ? false
      : isParamNameNestedEnumRelated(
          paramName,
          categorySearchConfig?.key,
          categorySearchConfig?.isNestedEnum
        );
    return isIndependentParam
      ? {
          ...validParams,
          ...validURLParamForExtendedData(
            paramName,
            paramValue,
            listingFieldFiltersConfig,
            defaultFiltersConfig
          ),
        }
      : dropNonFilterParams || isNestedEnum
      ? { ...validParams }
      : { ...validParams, [paramName]: paramValue };
  }, {});

  // TODO: Currently this only supports categoryLevel with nested param names.
  //       This needs more work to make other enum fields to understand nested keys.
  return { ...listingFieldsAndBuiltInFilterParamNames, ...validNestedCategoryParamNames };
};

/**
 * Helper to pick only valid values of search params from URL (location)
 * Note: location.search might look like: '?pub_category=men&pub_amenities=towels,bathroom'
 *
 * @param {Object} props object containing: location and (app) config
 * @returns picked search params against extended data config and default filter config
 */
export const validUrlQueryParamsFromProps = props => {
  const { location, config, params: currentPathParams = {} } = props;
  const { listingFields: listingFieldsConfig } = config?.listing || {};
  const { listingType: listingTypePathParam } = currentPathParams;
  const { defaultFilters: defaultFiltersRaw = [] } = config?.search || {};
  const defaultFiltersConfig = getSearchPageDefaultFiltersConfig(
    defaultFiltersRaw,
    listingTypePathParam
  );
  const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
  const listingCategories = config.categoryConfiguration.categories;
  const filterConfigs = {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
    activeListingTypes,
    currentPathParams,
  };

  // eslint-disable-next-line no-unused-vars
  const { mapSearch, page, dates, startDate, endDate, ...searchInURL } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const searchParamsWithoutTracking = stripIgnoredSearchQueryParams(searchInURL);
  // urlQueryParams doesn't contain page specific url params
  // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
  return validFilterParams(searchParamsWithoutTracking, filterConfigs, false);
};

/**
 * Helper to figure out initialValues for Final Form that handles search filters
 *
 * @param {Object} props object containing: location, listingFieldsConfig, defaultFiltersConfig
 * @param {Object} currentQueryParams object containing current state of queryParams (used only when isLiveEdit is false)
 * @returns a function with params queryParamNames, and isLiveEdit.
 *          It's called from FilterComponent and it returns initial values for the filter.
 */
export const initialValues = (props, currentQueryParams) => (queryParamNames, isLiveEdit) => {
  const urlQueryParams = validUrlQueryParamsFromProps(props);

  // Get initial value for a given parameter from state if its there.
  const getInitialValue = paramName => {
    // Query parameters that are in state (user might have not yet clicked "Apply")
    const currentQueryParam = currentQueryParams[paramName];
    const hasQueryParamInState = typeof currentQueryParam !== 'undefined';
    return hasQueryParamInState && !isLiveEdit ? currentQueryParam : urlQueryParams[paramName];
  };

  // Return all the initial values related to given queryParamNames
  // InitialValues for "amenities" filter could be
  // { amenities: "has_any:towel,jacuzzi" }
  const isArray = Array.isArray(queryParamNames);
  return isArray
    ? queryParamNames.reduce((acc, paramName) => {
        const initValue = getInitialValue(paramName);
        const addInitialValueMaybe = initValue ? { [paramName]: initValue } : {};
        return { ...acc, ...addInitialValueMaybe };
      }, {})
    : {};
};

/**
 * Some parameters could conflict with sort. If sortConfig defines conflictingFilters,
 * This function checks if they are active and returns "sort" param as null
 *
 * @param {*} searchParams
 * @param {*} filterConfigs contains config like listingFieldsConfig and defaultFiltersConfig
 * @param {*} sortConfig
 * @returns sort parameter as null if sortConfig defines conflictingFilters
 */
export const cleanSearchFromConflictingParams = (searchParams, filterConfigs, sortConfig) => {
  // Single out filters that should disable SortBy when an active
  // keyword search sorts the listings according to relevance.
  // In those cases, sort parameter should be removed.
  const sortingFiltersActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    searchParams,
    filterConfigs
  );

  // search params without category-restricted params
  const unlimitedSearchParams = omitLimitedListingFieldParams(searchParams, filterConfigs);

  return sortingFiltersActive
    ? { ...unlimitedSearchParams, [sortConfig.queryParamName]: null }
    : unlimitedSearchParams;
};

/**
 * Extract search parameters, including a custom URL params,
 * which are validated by mapping the values to marketplace custom config.
 *
 * @param {Object} params Search query params
 * @param {Object} listingFieldsConfig extended data configuration with indexForSearch === true
 * @param {Object} defaultFiltersConfig configuration for default built-in filters.
 * @param {Object} sortConfig config for sort search results feature
 * @param {boolean} isOriginInUse if origin is in use, return it too.
 */
export const pickSearchParamsOnly = (params, filterConfigs, sortConfig, isOriginInUse) => {
  const { address, origin, bounds, ...rest } = params || {};
  const boundsMaybe = bounds ? { bounds } : {};
  const originMaybe = isOriginInUse && origin ? { origin } : {};
  const filterParams = validFilterParams(rest, filterConfigs);
  const keywords = rest?.keywords;
  const keywordsMaybe =
    (typeof keywords === 'string' && keywords.trim().length > 0) ||
    (keywords !== null && typeof keywords !== 'undefined' && typeof keywords !== 'string')
      ? { keywords }
      : {};
  const sort = rest[sortConfig.queryParamName];
  const sortMaybe = sort ? { sort } : {};

  return {
    ...boundsMaybe,
    ...originMaybe,
    ...filterParams,
    ...keywordsMaybe,
    ...sortMaybe,
  };
};

/**
 * This helper has 2 functions:
 * - It picks search params from Location instance (location.search)
 * - It verifies that those search params are the same as search params in state.
 *   In some cases, search params are referencing previous params
 *   and listings should not be considered loaded.
 *
 * @param {Object} searchFromLocation searchParams from URL (location.search)
 * @param {Object} searchParamsInProps searchParams from store
 * @param {Object} listingFieldsConfig config for listing's extended data
 * @param {Object} defaultFiltersConfig config for default filters
 * @param {Object} sortConfig config for SortBy feature
 * @returns object containing
 *   1. searchParamsInURL (omit pagination 'page' or 'mapSearch'),
 *   2. urlQueryParams (picked valid search params for listing query), and
 *   3. searchParamsAreInSync is true if searchFromLocation and searchParamsInProps match.
 */
export const searchParamsPicker = (
  searchFromLocation,
  searchParamsInProps,
  filterConfigs,
  sortConfig,
  isOriginInUse
) => {
  const { mapSearch, page, ...searchParamsInURL } = parse(searchFromLocation, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Pick only search params that are part of current search configuration
  const queryParamsFromSearchParams = pickSearchParamsOnly(
    searchParamsInProps,
    filterConfigs,
    sortConfig,
    isOriginInUse
  );
  // Pick only search params that are part of current search configuration
  const queryParamsFromURL = pickSearchParamsOnly(
    searchParamsInURL,
    filterConfigs,
    sortConfig,
    isOriginInUse
  );

  // Page transition might initially use values from previous search
  const searchParamsAreInSync =
    stringify(queryParamsFromURL) === stringify(queryParamsFromSearchParams);

  return {
    urlQueryParams: queryParamsFromURL,
    searchParamsInURL,
    searchParamsAreInSync,
  };
};

const isActiveQueryParamValue = value => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && typeof value !== 'undefined';
};

export const getTotalItemsFromPagination = ({
  pagination,
  searchParamsAreInSync,
  loadedListingsCount = 0,
  context = 'search',
}) => {
  const rawTotalItems = pagination?.totalItems;
  const hasServerTotalItems = Number.isFinite(rawTotalItems);
  const totalItems =
    searchParamsAreInSync && hasServerTotalItems ? Number(rawTotalItems) : 0;

  if (
    process.env.NODE_ENV === 'development' &&
    searchParamsAreInSync &&
    !hasServerTotalItems &&
    loadedListingsCount > 0 &&
    typeof window !== 'undefined'
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[${context}] Missing pagination.totalItems; using 0 for total-count UI to avoid page-size count drift.`,
      { pagination, loadedListingsCount }
    );
  }

  return {
    totalItems,
    hasServerTotalItems,
  };
};

/**
 * Returns only active filter params that are explicitly present in query params.
 * Non-filter params (e.g. bounds/origin/page) are omitted.
 */
export const getActiveFiltersFromQuery = (queryParams = {}, filterConfigs) => {
  const validParams = validFilterParams(queryParams, filterConfigs, true);
  return Object.entries(validParams).reduce((picked, [key, value]) => {
    return isActiveQueryParamValue(value) ? { ...picked, [key]: value } : picked;
  }, {});
};

const hasValue = value => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && typeof value !== 'undefined';
};

/**
 * Detect if current search state differs from defaults.
 * "Dirty" means user has keywords, active filters, or non-default sorting.
 */
export const isSearchDirty = ({
  queryParams = {},
  filterConfigs,
  sortConfig,
}) => {
  const sortParamName = sortConfig?.queryParamName || 'sort';
  const defaultSortKey = sortConfig?.relevanceKey;
  const selectedSort = queryParams[sortParamName];
  const hasNonDefaultSort =
    hasValue(selectedSort) && (!defaultSortKey || selectedSort !== defaultSortKey);

  const keywords = queryParams?.keywords;
  const hasKeywords = hasValue(keywords);

  const filterParamNames = getQueryParamNames(
    filterConfigs?.listingFieldsConfig || [],
    filterConfigs?.defaultFiltersConfig || []
  );
  const ignoredParams = new Set([sortParamName, 'keywords']);
  const hasAnyActiveFilter = filterParamNames.some(
    paramName => !ignoredParams.has(paramName) && hasValue(queryParams[paramName])
  );

  return hasKeywords || hasAnyActiveFilter || hasNonDefaultSort;
};

export const pickListingFieldFilters = params => {
  const {
    listingFields,
    locationSearch,
    categoryConfiguration,
    activeListingTypes,
    currentPathParams = {},
  } = params;
  const searchParams = parse(locationSearch);
  const categories = categoryConfiguration.categories;
  const validNestedCategoryParamNames = categories
    ? validURLParamForCategoryData(categoryConfiguration.key, categories, 1, searchParams)
    : {};

  const { listingType: listingTypeParam } = currentPathParams;
  const listingTypeParamMaybe = listingTypeParam ? { pub_listingType: listingTypeParam } : {};
  const validListingTypeParamNames = activeListingTypes
    ? validURLParamForListingTypeData(activeListingTypes, {
        ...searchParams,
        ...listingTypeParamMaybe,
      })
    : {};

  const currentCategories = Object.values(validNestedCategoryParamNames);
  const currentListingType = currentListingTypeValues(
    currentPathParams,
    validListingTypeParamNames,
    activeListingTypes
  );
  const pickedFields = listingFields.reduce((picked, fieldConfig) => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    const isTargetListingField = isFieldForListingType(currentListingType, fieldConfig);
    return isTargetCategory && isTargetListingField ? [...picked, fieldConfig] : picked;
  }, []);
  return pickedFields;
};
/**
 * Returns listing fields (extended data configs) grouped into arrays. [primaryConfigArray, secondaryConfigArray]
 * @param {Object} configs listing extended data config
 * @param {Array<String>} activeListingTypes select configs that are marked only for these active listing types
 * @returns Array of grouped arrays. First subarray contains primary configs and the second contains secondary configs.
 */
export const groupListingFieldConfigs = (configs, activeListingTypes) =>
  configs.reduce(
    (grouped, config) => {
      const [primary, secondary] = grouped;
      const { listingTypeConfig = {}, filterConfig } = config;
      const isIndexed = filterConfig?.indexForSearch === true;
      const isActiveListingTypes =
        !listingTypeConfig.limitToListingTypeIds ||
        listingTypeConfig.listingTypeIds.some(lt => activeListingTypes.includes(lt));
      const isPrimary = filterConfig?.group === 'primary';
      return isActiveListingTypes && isIndexed && isPrimary
        ? [[...primary, config], secondary]
        : isActiveListingTypes && isIndexed
        ? [primary, [...secondary, config]]
        : grouped;
    },
    [[], []]
  );

const humanizeOption = value =>
  `${value}`
    .split(/[-_]/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Names people search for, for the main category pages. The site's own option labels are
// singular ("Leotard", "Pointe"), which read oddly in a page title.
const CATEGORY_PAGE_NAMES = {
  dancewear: 'Dancewear',
  costumes: 'Dance Costumes',
  shoes: 'Dance Shoes',
  'dancewear/leotard': 'Leotards',
  'dancewear/warmups': 'Warm-ups',
  'dancewear/skirt': 'Dance Skirts',
  'costumes/tutus': 'Tutus',
  'shoes/pointe': 'Pointe Shoes',
  'shoes/ballet': 'Ballet Shoes',
};

/**
 * The category a search page shows, as its shoppers would name it: the main category pages use
 * CATEGORY_PAGE_NAMES (e.g. "Pointe Shoes"); others the type label when a type is selected (from
 * the category's *_type listing field), otherwise the category name. Null on searches without a
 * category or type.
 *
 * @param {Object} searchParams search params from the URL (pub_category, pub_type)
 * @param {Object} config marketplace config
 * @returns {String|null}
 */
export const getSearchCategoryLabel = (searchParams, config) => {
  const categoryId = searchParams?.pub_category;
  const type = searchParams?.pub_type;
  if (!categoryId && !type) {
    return null;
  }
  const isSingle = value => typeof value === 'string' && value.length > 0 && !value.includes(',');
  const pageName =
    CATEGORY_PAGE_NAMES[
      [categoryId, type]
        .filter(isSingle)
        .join('/')
        .toLowerCase()
    ];
  if (pageName && (!type || isSingle(type)) && (!categoryId || isSingle(categoryId))) {
    return pageName;
  }

  if (isSingle(type)) {
    const typeFields = (config?.listing?.listingFields || []).filter(
      field =>
        /(^|_)type$/.test(field?.key || '') &&
        (!categoryId || (field?.categoryConfig?.categoryIds || []).includes(categoryId))
    );
    const typeOption = typeFields
      .flatMap(field => field?.enumOptions || [])
      .find(option => `${option?.option}`.toLowerCase() === type.toLowerCase());
    return typeOption?.label || humanizeOption(type);
  }

  if (isSingle(categoryId)) {
    const category = (config?.categoryConfiguration?.categories || []).find(
      c => c?.id === categoryId
    );
    return category?.name || humanizeOption(categoryId);
  }
  return null;
};

export const createSearchResultSchema = (
  listings,
  mainSearchData,
  intl,
  routeConfiguration,
  config,
  pageHeading
) => {
  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const marketplaceName = config.marketplaceName;
  const { address, keywords } = mainSearchData;
  const keywordsMaybe = keywords ? `"${keywords}"` : null;
  const searchTitle =
    address || keywordsMaybe || intl.formatMessage({ id: 'SearchPage.schemaForSearch' });
  // Category pages (e.g. /s?pub_category=dancewear&pub_type=Leotard) get their own title,
  // description and heading, so search engines can tell them apart
  const categoryLabel = keywords ? null : getSearchCategoryLabel(mainSearchData, config);
  const pageNumber = Number.parseInt(mainSearchData?.page, 10);
  const pageSuffix = pageNumber > 1 ? ` (page ${pageNumber})` : '';
  const schemaDescription = categoryLabel
    ? intl.formatMessage(
        { id: 'SearchPage.categorySchemaDescription' },
        { category: categoryLabel, marketplaceName }
      )
    : intl.formatMessage({ id: 'SearchPage.schemaDescription' });
  const schemaTitle = categoryLabel
    ? `${intl.formatMessage(
        { id: 'SearchPage.categorySchemaTitle' },
        { category: categoryLabel, marketplaceName }
      )}${pageSuffix}`
    : intl.formatMessage(
        { id: 'SearchPage.schemaTitle' },
        { searchTitle, marketplaceName, h1: pageHeading }
      );
  const heading = categoryLabel
    ? intl.formatMessage({ id: 'SearchPage.categoryHeading' }, { category: categoryLabel })
    : keywords
    ? intl.formatMessage({ id: 'SearchPage.keywordsHeading' }, { keywords })
    : intl.formatMessage({ id: 'SearchPage.defaultHeading' });

  const schemaListings = listings.map((l, i) => {
    const title = l.attributes.title;
    return {
      '@type': 'ListItem',
      // Positions start at 1; the URL is the listing's canonical address (/l/{id})
      position: i + 1,
      url: `${config.marketplaceRootURL}/l/${l.id.uuid}`,
      name: title,
    };
  });

  // An object, not a JSON string: search engines can't read structured data inside a string
  const schemaMainEntity = {
    '@type': 'ItemList',
    name: categoryLabel || searchTitle,
    itemListOrder: 'http://schema.org/ItemListOrderAscending',
    itemListElement: schemaListings,
  };
  return {
    title: schemaTitle,
    description: schemaDescription,
    heading,
    schema: {
      '@context': 'http://schema.org',
      '@type': 'SearchResultsPage',
      description: schemaDescription,
      name: schemaTitle,
      mainEntity: schemaMainEntity,
    },
  };
};

export const getDatesAndSeatsMaybe = (currentParams, newParams) => {
  const { seats, dates: newDates } = newParams;
  const { dates: currentDates } = currentParams;

  // Determine which dates and seats to use:
  // - if newDates has a value, it was just selected
  // - if newDates is null, it was just cleared
  // - if newDates is undefined, it was not modified, and we use currentDates
  const dates = !!newDates || newDates === null ? newDates : currentDates;

  const today = stringifyDateToISO8601(new Date());
  const aWeekFromNow = stringifyDateToISO8601(addTime(today, 7, 'day'));
  // Get parameters for dates and seats:
  // - If both dates and seats are included, pass both
  // - Dates can be queried without seats
  // - Seats cannot be queried without dates – pass a default date range
  //   of one week with the provided seats value
  // - If neither dates nor seats exist, set them to null to clear them from search
  const datesAndSeatsMaybe =
    dates && seats
      ? { dates, seats }
      : dates
      ? { dates }
      : seats
      ? { seats, dates: `${today},${aWeekFromNow}` }
      : { seats: null, dates: null };
  return datesAndSeatsMaybe;
};

/**
 * Returns params for createResourceLocatorString function based on the current
 * location and route configuration
 * @param {*} routes current route configuration
 * @param {*} location current ReactRouter location
 * @returns an object with the attributes routeName and pathParams, which can then be passed
 * as the corresponding parameters to createResourceLocatorString
 */
export const getSearchPageResourceLocatorStringParams = (routes, location) => {
  const matchedRoutes = matchPathname(location.pathname, routes);
  const searchPageRoute = 'SearchPage';
  const searchPageListingTypeRoute = 'SearchPageWithListingType';
  const costumeSearchPageRoute = 'CostumeSearchPage';

  if (matchedRoutes.length > 0) {
    const matched = matchedRoutes[0];
    const { params: pathParams, route } = matched;
    const routeName =
      route.name === searchPageListingTypeRoute
        ? searchPageListingTypeRoute
        : route.name === costumeSearchPageRoute
        ? costumeSearchPageRoute
        : searchPageRoute;

    return {
      routeName,
      pathParams,
    };
  } else {
    console.error(`Route not found for pathname ${location.pathname}, redirecting to SearchPage`);
    return {
      routeName: searchPageRoute,
      pathParams: {},
    };
  }
};

export const getActiveListingTypes = (config, listingTypePathParam) => {
  const availableListingTypes =
    config?.listing?.listingTypes
      ?.map(listingTypeConfig => listingTypeConfig.listingType || listingTypeConfig.id)
      .filter(Boolean) || [];
  const activeListingTypes = listingTypePathParam
    ? availableListingTypes.filter(lt => lt === listingTypePathParam)
    : availableListingTypes;
  return { activeListingTypes };
};
